import type { GetMissionsFromMapDto } from '../../api/api.g';
import type { MissionCardProps } from '../../components/common/mission/MissionCard';
import { FilterType } from '../MissionFilterType';

type ListingIdentity = { branchId?: string; id?: string; listingKey?: string; isFlashMission?: boolean; upcomingPeriod?: boolean; upcomingPeriodStartDate?: Date };

export function getMissionListingKey(mission: ListingIdentity): string {
  return mission.listingKey ?? `${mission.branchId ?? mission.id}:${mission.isFlashMission ? 'flash' : 'classic'}:${mission.upcomingPeriod ? `upcoming:${mission.upcomingPeriodStartDate?.toISOString() ?? ''}` : 'current'}`;
}

export function matchesMissionTypeFilter(
  mission: { isFlashMission?: boolean; isPinned?: boolean; pinned?: boolean; hasAgeRestrictedMissions?: boolean },
  filter?: FilterType,
): boolean {
  switch (filter) {
    case FilterType.Classic:
      return !mission.isFlashMission;
    case FilterType.Flash:
      return !!mission.isFlashMission;
    case FilterType.Featured:
      return !!(mission.isPinned || mission.pinned);
    case FilterType.AgeRestricted:
      return !!mission.hasAgeRestrictedMissions;
    default:
      return true;
  }
}

/** Pinned visibility does not override distance ordering in the shopper list. */
export function getDistanceSortedMissionCards(
  pinned: MissionCardProps[],
  regular: MissionCardProps[],
  flash: MissionCardProps[],
  filter?: FilterType,
): MissionCardProps[] {
  const unique = new Map<string, MissionCardProps>();
  for (const card of [...pinned.map(card => ({ ...card, pinned: true })), ...regular, ...flash]) {
    const key = getMissionListingKey(card);
    if (matchesMissionTypeFilter(card, filter) && !unique.has(key)) unique.set(key, card);
  }
  const distance = (card: MissionCardProps) =>
    card.distanceMeters !== undefined && Number.isFinite(card.distanceMeters) && card.distanceMeters >= 0
      ? card.distanceMeters
      : Number.POSITIVE_INFINITY;
  // Stable ties preserve the API's current/upcoming order; never sort formatted text.
  return [...unique.values()].sort((left, right) => distance(left) - distance(right));
}

export function partitionMissionListings(result: {
  highOrderMissions?: GetMissionsFromMapDto[];
  missions?: GetMissionsFromMapDto[];
  lowOrderMissions?: GetMissionsFromMapDto[];
}) {
  const highOrderKeys = new Set((result.highOrderMissions ?? []).map(getMissionListingKey));
  const unique = new Map<string, GetMissionsFromMapDto>();
  for (const mission of [...(result.highOrderMissions ?? []), ...(result.missions ?? []), ...(result.lowOrderMissions ?? [])]) {
    unique.set(getMissionListingKey(mission), mission);
  }
  const all = [...unique.values()];
  const isPinned = (mission: GetMissionsFromMapDto) => mission.isPinned || highOrderKeys.has(getMissionListingKey(mission));
  return {
    all,
    pinned: all.filter(isPinned),
    regular: all.filter(mission => !isPinned(mission) && !mission.isFlashMission),
    flash: all.filter(mission => !isPinned(mission) && mission.isFlashMission),
  };
}

export function getLocationMarkerMissions(missions: GetMissionsFromMapDto[]): GetMissionsFromMapDto[] {
  const branches = new Map<string, GetMissionsFromMapDto>();
  const rank = (mission: GetMissionsFromMapDto) =>
    (mission.upcomingPeriod ? 0 : 2) + (mission.isAvailableForDiscovery ?? mission.canAccept ? 1 : 0);
  for (const mission of missions) {
    const current = branches.get(mission.branchId);
    if (!current || rank(mission) > rank(current)) branches.set(mission.branchId, mission);
  }
  return [...branches.values()];
}
